import { getMonthsBetween } from './dateHelpers';

/**
 * Generate a monthly rotation schedule for residents
 * @param {Array} filteredResidents - Array of resident objects
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 * @param {Array} selectedRotations - Array of rotation names to assign
 * @param {Object} rotationFrequency - Object mapping rotation names to frequency counts
 * @param {Array} recurrentRotations - Array of rotations that can be repeated
 * @returns {Object} Schedule object with resident IDs as keys
 */
export const generateMonthlySchedule = async (
  filteredResidents,
  startDate,
  endDate,
  selectedRotations,
  rotationFrequency,
  recurrentRotations
) => {
  // Get all months between start and end date
  const months = getMonthsBetween(startDate, endDate);
  const totalMonths = months.length;

  if (totalMonths === 0) {
    throw new Error("No valid months found between start and end dates");
  }

  // Initialize schedule
  const newSchedule = {};
  filteredResidents.forEach((resident) => {
    newSchedule[resident.id] = {
      name: `${resident.firstName} ${resident.lastName}`,
      year: parseInt(resident.year),
      rotations: {},
    };
    months.forEach((month) => {
      newSchedule[resident.id].rotations[month] = null;
    });
  });

  // Create rotation assignments with even distribution
  const rotationAssignments = [];
  
  // Calculate total rotation count including frequency
  const totalRotationCount = selectedRotations.reduce((total, rotation) => {
    const frequency = rotationFrequency[rotation] || 1;
    return total + frequency;
  }, 0);
  
  // Create expanded rotation list based on frequency
  const expandedRotations = [];
  selectedRotations.forEach(rotation => {
    const frequency = rotationFrequency[rotation] || 1;
    for (let i = 0; i < frequency; i++) {
      expandedRotations.push(rotation);
    }
  });
  
  if (totalRotationCount === totalMonths) {
    // Perfect match - distribute evenly
    rotationAssignments.push(...expandedRotations);
  } else if (totalRotationCount < totalMonths) {
    // We need to repeat some rotations - distribute them evenly
    const rotationsToRepeat = recurrentRotations.length > 0 
      ? recurrentRotations 
      : selectedRotations;
    
    // Create expanded list of rotations to repeat
    const expandedRotationsToRepeat = [];
    rotationsToRepeat.forEach(rotation => {
      const frequency = rotationFrequency[rotation] || 1;
      for (let i = 0; i < frequency; i++) {
        expandedRotationsToRepeat.push(rotation);
      }
    });
    
    // Calculate how many times each rotation should appear
    const rotationCounts = {};
    expandedRotationsToRepeat.forEach(rotation => {
      rotationCounts[rotation] = (rotationCounts[rotation] || 0) + 1;
    });
    
    // Calculate how many additional times each rotation needs to be repeated
    const additionalCounts = {};
    Object.keys(rotationCounts).forEach(rotation => {
      const currentCount = rotationCounts[rotation];
      const totalNeeded = Math.ceil(totalMonths / expandedRotationsToRepeat.length) * currentCount;
      additionalCounts[rotation] = Math.max(0, totalNeeded - currentCount);
    });
    
    // Create a distribution plan
    const distributionPlan = [];
    Object.keys(additionalCounts).forEach(rotation => {
      const additional = additionalCounts[rotation];
      for (let i = 0; i < additional; i++) {
        distributionPlan.push(rotation);
      }
    });
    
    // Shuffle the distribution plan for randomness
    const shuffledPlan = distributionPlan.sort(() => Math.random() - 0.5);
    
    // Combine original rotations with additional ones
    rotationAssignments.push(...expandedRotationsToRepeat, ...shuffledPlan);
    
    // Trim to exact number of months needed
    if (rotationAssignments.length > totalMonths) {
      rotationAssignments.splice(totalMonths);
    } else if (rotationAssignments.length < totalMonths) {
      // Fill remaining slots with rotations that need more coverage
      const remainingSlots = totalMonths - rotationAssignments.length;
      const rotationsNeedingMore = Object.keys(additionalCounts).filter(rotation => 
        additionalCounts[rotation] > 0
      );
      
      for (let i = 0; i < remainingSlots; i++) {
        const rotationToAdd = rotationsNeedingMore[i % rotationsNeedingMore.length];
        rotationAssignments.push(rotationToAdd);
      }
    }
  } else {
    // More rotations than months - select evenly distributed subset
    const rotationsPerMonth = Math.floor(totalMonths / selectedRotations.length);
    const remainingSlots = totalMonths % selectedRotations.length;
    
    selectedRotations.forEach(rotation => {
      const frequency = rotationFrequency[rotation] || 1;
      const slotsForThisRotation = rotationsPerMonth + (remainingSlots > 0 ? 1 : 0);
      const actualSlots = Math.min(slotsForThisRotation, frequency);
      
      for (let i = 0; i < actualSlots; i++) {
        rotationAssignments.push(rotation);
      }
      if (remainingSlots > 0) {
        remainingSlots--;
      }
    });
    
    // Fill any remaining slots with rotations that have frequency > 1
    while (rotationAssignments.length < totalMonths) {
      const highFrequencyRotations = selectedRotations.filter(rotation => 
        (rotationFrequency[rotation] || 1) > 1
      );
      if (highFrequencyRotations.length > 0) {
        const rotationToAdd = highFrequencyRotations[Math.floor(Math.random() * highFrequencyRotations.length)];
        rotationAssignments.push(rotationToAdd);
      } else {
        // Fallback to any rotation
        const randomRotation = selectedRotations[Math.floor(Math.random() * selectedRotations.length)];
        rotationAssignments.push(randomRotation);
      }
    }
  }

  // Assign rotations to residents with constraints
  const residentIds = Object.keys(newSchedule);
  // Track how many times each rotation is assigned per month
  const rotationCountPerMonth = {};
  months.forEach(month => {
    rotationCountPerMonth[month] = {};
    selectedRotations.forEach(rotation => {
      rotationCountPerMonth[month][rotation] = 0;
    });
  });
  // Track each resident's rotation usage
  const residentRotationUsage = {};
  residentIds.forEach(residentId => {
    residentRotationUsage[residentId] = {};
    selectedRotations.forEach(rotation => {
      residentRotationUsage[residentId][rotation] = 0;
    });
  });
  // Assign rotations with even distribution across months
  residentIds.forEach((residentId, residentIndex) => {
    const resident = filteredResidents[residentIndex];
    let previousRotation = null;
    
    // Create a rotation schedule for this resident that ensures even distribution
    const residentRotationSchedule = [];
    const rotationFrequencyCopy = { ...rotationFrequency };
    
    // First, assign rotations based on their frequency
    selectedRotations.forEach(rotation => {
      const frequency = rotationFrequencyCopy[rotation] || 1;
      for (let i = 0; i < frequency; i++) {
        residentRotationSchedule.push(rotation);
      }
    });
    
    // If we have more rotations than months, trim the schedule
    if (residentRotationSchedule.length > totalMonths) {
      residentRotationSchedule.splice(totalMonths);
    } else if (residentRotationSchedule.length < totalMonths) {
      // Fill remaining slots with rotations that need more coverage
      const remainingSlots = totalMonths - residentRotationSchedule.length;
      const rotationsToRepeat = recurrentRotations.length > 0 ? recurrentRotations : selectedRotations;
      
      for (let i = 0; i < remainingSlots; i++) {
        const rotationToAdd = rotationsToRepeat[i % rotationsToRepeat.length];
        residentRotationSchedule.push(rotationToAdd);
      }
    }
    
    // Shuffle the schedule to avoid patterns
    const shuffledSchedule = [...residentRotationSchedule].sort(() => Math.random() - 0.5);
    
    months.forEach((month, monthIndex) => {
      // Get available rotations for this month, respecting constraints
      let availableRotations = shuffledSchedule.filter(rotation => {
        // No back-to-back for same resident
        if (previousRotation === rotation) return false;
        // Respect resident's frequency
        if (residentRotationUsage[residentId][rotation] >= (rotationFrequency[rotation] || 1)) return false;
        // Enforce max 2 per month for this rotation
        if (rotationCountPerMonth[month][rotation] >= 2) return false;
        return true;
      });
      
      // If not enough available rotations, expand the search
      if (availableRotations.length === 0) {
        availableRotations = selectedRotations.filter(rotation => {
          if (previousRotation === rotation) return false;
          if (residentRotationUsage[residentId][rotation] >= (rotationFrequency[rotation] || 1)) return false;
          return true;
        });
      }
      
      // If still not enough, use any rotation
      if (availableRotations.length === 0) {
        availableRotations = selectedRotations;
      }
      
      // Prefer rotations from the resident's schedule that haven't been overused this month
      const preferredRotations = availableRotations.filter(rotation => 
        shuffledSchedule.includes(rotation) && rotationCountPerMonth[month][rotation] < 2
      );
      
      const rotationsToChooseFrom = preferredRotations.length > 0 ? preferredRotations : availableRotations;
      
      // Pick the rotation with the least assignments this month
      let minCount = Math.min(...rotationsToChooseFrom.map(r => rotationCountPerMonth[month][r]));
      let leastUsed = rotationsToChooseFrom.filter(r => rotationCountPerMonth[month][r] === minCount);
      const assignedRotation = leastUsed[Math.floor(Math.random() * leastUsed.length)];
      
      // Assign the rotation
      newSchedule[residentId].rotations[month] = assignedRotation;
      rotationCountPerMonth[month][assignedRotation]++;
      residentRotationUsage[residentId][assignedRotation]++;
      previousRotation = assignedRotation;
    });
  });
  return newSchedule;
};

